import { mdiGavel, mdiAlertOutline } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import type { ContractCoverage } from '../../api/schema'
import { useLocale } from '../../contexts/LocaleContext'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { authorizationDate, coverageState, formatCents, mainAuthorization } from './coverage-utils'

/**
 * One line saying which resolution covers a commitment (spec 2026-10-05-02):
 * "Covered by resolution no. 7 of 12 March, ceiling €240K, spent €187K". Shared
 * by the contract page and the intervention page.
 */
export function CoverageSummary({ coverage, testId = 'coverage-summary' }: { coverage?: ContractCoverage; testId?: string }) {
    const { t } = useTranslation('assemblies')
    const { locale } = useLocale()
    if (!coverage) return null

    const state = coverageState(coverage)
    const main = mainAuthorization(coverage)
    const currency = main?.currency
    const money = (cents: number) => formatCents(cents, currency, locale)

    const parts: string[] = []
    if (state === 'covered') {
        const date = authorizationDate(main, locale)
        parts.push(
            main?.resolution_number != null && date
                ? t('coverage.covered', { number: main.resolution_number, date })
                : t('coverage.covered_plain'),
        )
        if (coverage.ceiling_cents != null) parts.push(t('coverage.ceiling', { ceiling: money(coverage.ceiling_cents) }))
        parts.push(t('coverage.consumed', { consumed: money(coverage.consumed_cents) }))
    } else if (state === 'pending') {
        const reason = main?.reason ? t(`coverage.reason.${main.reason}`) : ''
        parts.push(reason ? `${t('coverage.pending')} (${reason})` : t('coverage.pending'))
    } else {
        parts.push(t('coverage.none'))
    }

    return (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid={testId} data-state={state}>
            <AppIcon path={mdiGavel} size="sm" className="shrink-0" aria-hidden="true" />
            <Badge variant={state === 'covered' ? 'success' : state === 'pending' ? 'warning' : 'secondary'}>
                {parts.join(', ')}
            </Badge>
            {coverage.exceeded && (
                <Badge variant="destructive" data-testid={`${testId}-exceeded`}>
                    <AppIcon path={mdiAlertOutline} size="xs" aria-hidden="true" />
                    {t('coverage.exceeded')}
                </Badge>
            )}
        </div>
    )
}
